// src/components/admin/CreateSalesUserModal.jsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { XCircle, CheckCircle, UserPlus, Eye, EyeOff } from "lucide-react";
import ModalPortal from "../shared/ui/ModalPortal";

const empty = () => ({
  fullName: "",
  email: "",
  password: "",
  phoneNumber: "",
  smtpEmail: "",
  smtpPassword: "",
  smtpHost: "",
  smtpPort: "",
});

const getPasswordStrength = (p) => {
  if (!p) return { strength: 0, text: "", color: "" };
  let s = 0;
  if (p.length >= 6) s++;
  if (p.length >= 8) s++;
  if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(p)) s++;
  if (s <= 2) return { strength: 33, color: "bg-red-500" };
  if (s <= 3) return { strength: 66, color: "bg-yellow-500" };
  return { strength: 100, color: "bg-green-500" };
};

export default function CreateSalesUserModal({ onClose, onSubmit }) {
  const { t } = useTranslation();

  const [form, setForm] = useState(empty());
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const showMsg = (type, text) => setMessage({ type, text });
  const clearMsg = () => setMessage({ type: "", text: "" });

  const validate = () => {
    if (!form.fullName.trim()) return t("admin.createSalesUser.validation.fullNameRequired");
    if (!form.email.trim()) return t("admin.createSalesUser.validation.emailRequired");
    if (!form.password) return t("admin.createSalesUser.validation.passwordRequired");
    if (form.password.length < 6) return t("validation.passwordMinLength", { count: 6 });
    if (!form.smtpEmail.trim()) return t("admin.createSalesUser.validation.smtpEmailRequired");
    if (!form.smtpPassword) return t("admin.createSalesUser.validation.smtpPasswordRequired");
    return "";
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { showMsg("error", err); return; }

    setSaving(true);
    clearMsg();
    try {
      const ok = await onSubmit({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        password: form.password,
        phoneNumber: form.phoneNumber.trim() || undefined,
        smtpEmail: form.smtpEmail.trim(),
        smtpPassword: form.smtpPassword,
        smtpHost: form.smtpHost.trim() || undefined,
        smtpPort: form.smtpPort ? Number(form.smtpPort) : undefined,
      });

      if (ok) {
        showMsg("success", t("admin.createSalesUser.successMessage"));
        setTimeout(() => onClose(), 1500);
      } else {
        showMsg("error", t("admin.createSalesUser.errorMessage"));
      }
    } catch (e) {
      showMsg("error", e?.message || t("admin.createSalesUser.errorMessage"));
    } finally {
      setSaving(false);
    }
  };

  const pwStrength = getPasswordStrength(form.password);

  return (
    <ModalPortal onClose={saving ? undefined : onClose}>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-md"
        onMouseDown={saving ? undefined : onClose}
      />

      {/* Dialog */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div
          className="relative w-full max-w-lg bg-white/10 backdrop-blur-2xl border border-white/20 shadow-[0_0_40px_rgba(255,0,255,0.30)] rounded-2xl overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          {/* Header */}
          <div className="p-6 bg-gradient-to-r from-pink-600 to-purple-700 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <UserPlus className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-xl font-bold text-white">{t("admin.createSalesUser.modalTitle")}</h2>
                <p className="text-sm text-white/80">{t("admin.createSalesUser.modalSubtitle")}</p>
              </div>
            </div>
            <button
              onClick={saving ? undefined : onClose}
              disabled={saving}
              className="text-white hover:text-gray-200 disabled:opacity-50"
              type="button"
            >
              <XCircle className="w-7 h-7" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4 text-white">
            {/* Message banner */}
            {message.text && (
              <div className={`p-3 rounded-xl text-sm flex items-start gap-3 border ${
                message.type === "success"
                  ? "bg-green-500/10 border-green-400 text-green-200"
                  : "bg-red-500/10 border-red-400 text-red-200"
              }`}>
                {message.type === "success"
                  ? <CheckCircle className="w-5 h-5 shrink-0" />
                  : <XCircle className="w-5 h-5 shrink-0" />
                }
                <span>{message.text}</span>
              </div>
            )}

            {/* Full Name */}
            <div>
              <label className="block text-sm mb-1">{t("admin.createSalesUser.fullNameLabel")}</label>
              <input
                type="text"
                value={form.fullName}
                onChange={set("fullName")}
                placeholder={t("admin.createSalesUser.fullNamePlaceholder")}
                disabled={saving}
                className={inputCls}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm mb-1">{t("admin.createSalesUser.emailLabel")}</label>
              <input
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder={t("admin.createSalesUser.emailPlaceholder")}
                disabled={saving}
                className={inputCls}
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm mb-1">{t("admin.createSalesUser.passwordLabel")}</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={set("password")}
                  placeholder={t("admin.createSalesUser.passwordPlaceholder")}
                  disabled={saving}
                  className={inputCls + " pr-10"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Password strength bar */}
              {form.password && (
                <div className="mt-2">
                  <div className="w-full bg-white/10 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${pwStrength.color}`}
                      style={{ width: `${pwStrength.strength}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm mb-1">{t("admin.createSalesUser.phoneLabel")}</label>
              <input
                type="tel"
                value={form.phoneNumber}
                onChange={set("phoneNumber")}
                placeholder={t("admin.createSalesUser.phonePlaceholder")}
                disabled={saving}
                className={inputCls}
              />
            </div>

            {/* SMTP Email */}
            <div>
              <label className="block text-sm mb-1">{t("admin.createSalesUser.smtpEmailLabel")}</label>
              <input
                type="email"
                value={form.smtpEmail}
                onChange={set("smtpEmail")}
                placeholder={t("admin.createSalesUser.smtpEmailPlaceholder")}
                disabled={saving}
                className={inputCls}
              />
            </div>

            {/* SMTP Password */}
            <div>
              <label className="block text-sm mb-1">{t("admin.createSalesUser.smtpPasswordLabel")}</label>
              <input
                type="password"
                value={form.smtpPassword}
                onChange={set("smtpPassword")}
                placeholder={t("admin.createSalesUser.smtpPasswordPlaceholder")}
                disabled={saving}
                className={inputCls}
              />
            </div>

            {/* SMTP Host + Port */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-sm mb-1">{t("admin.createSalesUser.smtpHostLabel")}</label>
                <input
                  type="text"
                  value={form.smtpHost}
                  onChange={set("smtpHost")}
                  placeholder={t("admin.createSalesUser.smtpHostPlaceholder")}
                  disabled={saving}
                  className={inputCls}
                />
              </div>
              <div className="w-28">
                <label className="block text-sm mb-1">{t("admin.createSalesUser.smtpPortLabel")}</label>
                <input
                  type="number"
                  value={form.smtpPort}
                  onChange={set("smtpPort")}
                  placeholder="587"
                  disabled={saving}
                  className={inputCls}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={saving ? undefined : onClose}
                disabled={saving}
                className="flex-1 py-3 rounded-xl font-semibold bg-gray-600 hover:bg-gray-700 text-white disabled:opacity-40"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="flex-1 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 to-red-500 hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t("admin.createSalesUser.creating")}
                  </>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5" />
                    {t("admin.createSalesUser.submit")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30 placeholder-white/40 disabled:opacity-50";
